// Plan madde 8.2 — error → toast adapter.
//
// Translates a thrown EnvelopeError (or any unknown caught value) into a
// toast descriptor: variant + i18n keys + retry hints + trace_id. The
// descriptor is framework-agnostic; showErrorToast() pipes it through
// sonner's toast.* API + i18next's t() function for the actual UI render.
//
// i18n key resolution order (most specific → fallback):
//   1. errors.code.<primary.code>            — per-code custom message
//   2. errors.classification.<class>.fallback — per-class generic message
//   3. errors.unknown.fallback               — when no envelope was parsed
// Title resolution mirrors the same chain (errors.code.<code>__title fallback
// to errors.classification.<class>.title fallback to errors.unknown.title).
//
// Variant mapping per spec 58 classification:
//   error  : authentication / authorization / dependency / unavailable / internal
//   warning: validation / conflict / rate_limited
//   info   : not_found
// (rate_limited is a warning rather than info because the user almost
// always needs to slow down — info would be too quiet.)

import type { TFunction } from "i18next";
import { toast } from "sonner";

import type { Classification } from "@novemai/error-envelope";

import { EnvelopeError } from "@/api/envelope";

export type ToastVariant = "error" | "warning" | "info";

export interface ToastDescriptor {
  readonly variant: ToastVariant;
  /** i18n key to resolve for the toast title. */
  readonly titleKey: string;
  /** i18n key to resolve for the toast description / body. */
  readonly messageKey: string;
  /** Server-suggested retry delay (ms); set on rate_limited / dependency / unavailable. */
  readonly retryAfterMs?: number;
  /** trace_id from envelope.meta — quoted in the description for support reference. */
  readonly traceId?: string;
  /** Original primary error code — useful for analytics + GlitchTip tagging. */
  readonly code?: string;
  /** Classification — passed through for downstream consumers (logger, analytics). */
  readonly classification?: Classification;
}

const CLASSIFICATION_VARIANT: Record<Classification, ToastVariant> = {
  authentication: "error",
  authorization: "error",
  validation: "warning",
  not_found: "info",
  conflict: "warning",
  rate_limited: "warning",
  dependency: "error",
  internal: "error",
  unavailable: "error",
};

/**
 * Pure adapter — caught error → toast descriptor. Safe to call from
 * any callback (TanStack Query onError, mutation onError, ErrorBoundary
 * componentDidCatch). Does NOT render a toast — pair with showErrorToast()
 * or call sonner's toast API yourself with the resolved keys.
 */
export function mapErrorToToast(error: unknown): ToastDescriptor {
  if (error instanceof EnvelopeError) {
    const primary = error.primary;
    if (primary === undefined) {
      // Synthetic edge case — EnvelopeError with empty errors[]; fall
      // back to the unknown bucket so something still renders.
      return {
        variant: "error",
        titleKey: "errors.unknown.title",
        messageKey: "errors.unknown.fallback",
        ...(error.meta?.trace_id !== undefined
          ? { traceId: error.meta.trace_id }
          : {}),
      };
    }
    const variant = CLASSIFICATION_VARIANT[primary.classification];
    return {
      variant,
      titleKey: `errors.classification.${primary.classification}.title`,
      messageKey: `errors.code.${primary.code}`,
      ...(primary.retry_after_ms !== undefined
        ? { retryAfterMs: primary.retry_after_ms }
        : {}),
      ...(error.meta?.trace_id !== undefined
        ? { traceId: error.meta.trace_id }
        : {}),
      code: primary.code,
      classification: primary.classification,
    };
  }
  // Non-envelope thrown value (network throw outside the unwrap chain,
  // unhandled JS error, etc.) — surface a generic unknown toast.
  return {
    variant: "error",
    titleKey: "errors.unknown.title",
    messageKey: "errors.unknown.fallback",
  };
}

/**
 * Render the toast through sonner. Resolves the i18n keys via the
 * provided t() function (caller passes from useTranslation()), with a
 * graceful fallback chain if a specific code key isn't translated yet:
 *   errors.code.<code> → errors.classification.<class>.fallback → errors.unknown.fallback
 *
 * The fallback chain matters because backend ships new error codes
 * faster than the frontend can land translations — a missing key
 * shouldn't render an empty toast or the literal key string.
 */
export function showErrorToast(error: unknown, t: TFunction): void {
  const descriptor = mapErrorToToast(error);

  // Translation with fallback chain. i18next's `defaultValue` option
  // gives us the per-call fallback; the t() result is the resolved
  // string when the key exists, or the defaultValue when it doesn't.
  const classFallbackKey =
    descriptor.classification !== undefined
      ? `errors.classification.${descriptor.classification}.fallback`
      : "errors.unknown.fallback";

  const title = t(descriptor.titleKey, {
    defaultValue: t("errors.unknown.title"),
  });
  const message = t(descriptor.messageKey, {
    defaultValue: t(classFallbackKey, {
      defaultValue: t("errors.unknown.fallback"),
    }),
  });

  // Append trace_id to the description in a small, monospace-friendly
  // suffix so support can quote it. We do NOT translate the "Trace:"
  // label — it's a debugging affordance, not user-facing copy.
  const description = descriptor.traceId
    ? `${message}\nTrace: ${descriptor.traceId}`
    : message;

  // sonner's toast.* return id; we ignore it — auto-dismiss handles
  // lifecycle. Plan madde 11 will add an "Action" button surface for
  // step-up auth (e.g., toast.error(..., { action: { label: "Sign in", onClick: ... } })).
  switch (descriptor.variant) {
    case "error":
      toast.error(title, { description });
      break;
    case "warning":
      toast.warning(title, { description });
      break;
    case "info":
      toast.info(title, { description });
      break;
  }
}
